import styled from "styled-components";
import { fade } from "@material-ui/core/styles";
import {
  cuideoTheme,
  COLOR_BORDER,
  COLOR_BORDER_LIGHT,
  COLOR_ERROR_HOVER,
  COLOR_EVENT_LABEL,
  COLOR_SURFACE_DISABLED,
  COLOR_SURFACE_SOFT,
} from "../../../../containers/themes/defaultTheme";
import { SLOT_HEIGHT, TIME_COLUMN_WIDTH } from "./constants";

const SECONDARY = cuideoTheme.palette.secondary.main;

// Rejilla de fondo de las columnas del día: línea fina cada media hora y línea
// más marcada en cada hora completa.
const SLOT_GRID = `repeating-linear-gradient(
    to bottom,
    transparent 0px,
    transparent ${SLOT_HEIGHT - 1}px,
    ${COLOR_BORDER_LIGHT} ${SLOT_HEIGHT - 1}px,
    ${COLOR_BORDER_LIGHT} ${SLOT_HEIGHT}px,
    transparent ${SLOT_HEIGHT}px,
    transparent ${2 * SLOT_HEIGHT - 1}px,
    ${COLOR_BORDER} ${2 * SLOT_HEIGHT - 1}px,
    ${COLOR_BORDER} ${2 * SLOT_HEIGHT}px
  )`;

export const Container = styled.div<{ $dragging: boolean }>`
  width: 100%;
  overflow-x: auto;
  border: 1px solid ${COLOR_BORDER};
  border-radius: 5px;
  margin-top: 0.5rem;
  font-family: "Source Sans Pro", Helvetica, Arial, sans-serif;
  ${({ $dragging }) => $dragging && "user-select: none;"}
`;

export const ScrollArea = styled.div`
  max-height: 600px;
  overflow-y: auto;
`;

export const CalendarLayout = styled.div`
  min-width: 500px;
`;

export const HeaderBar = styled.div`
  display: flex;
  position: sticky;
  top: 0;
  z-index: 3;
`;

export const TimeHeader = styled.div`
  width: ${TIME_COLUMN_WIDTH}px;
  flex-shrink: 0;
  background: ${cuideoTheme.palette.primary.main};
  padding: 6px 0;
`;

export const DayHeaderCell = styled.div<{ $disabled: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: ${cuideoTheme.palette.primary.main};
  color: #fff;
  font-size: calc(0.75rem * var(--wc-font-scale, 1));
  font-weight: 700;
  padding: 4px 0;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

export const CopyDayButton = styled.button<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  color: #fff;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.25 : 0.7)};
  padding: 2px 6px;
  line-height: 1;
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 0.25 : 1)};
  }
`;

export const BodyArea = styled.div`
  display: flex;
`;

export const TimeColumn = styled.div`
  width: ${TIME_COLUMN_WIDTH}px;
  flex-shrink: 0;
`;

export const TimeLabel = styled.div<{ $isHour: boolean }>`
  height: ${SLOT_HEIGHT}px;
  box-sizing: border-box;
  font-size: calc(0.625rem * var(--wc-font-scale, 1));
  color: ${cuideoTheme.palette.primary.main};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  padding-right: 4px;
  border-right: 1px solid ${COLOR_BORDER};
  border-bottom: ${({ $isHour }) =>
    $isHour ? `1px solid ${COLOR_BORDER_LIGHT}` : `1px solid ${COLOR_BORDER}`};
  background: ${COLOR_SURFACE_SOFT};
`;

export const DayColumn = styled.div<{ $disabled: boolean; $slots: number }>`
  flex: 1;
  position: relative;
  height: ${({ $slots }) => $slots * SLOT_HEIGHT}px;
  border-right: 1px solid ${COLOR_BORDER_LIGHT};
  background-color: ${({ $disabled }) =>
    $disabled ? COLOR_SURFACE_DISABLED : "#fff"};
  background-image: ${SLOT_GRID};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  &:last-child { border-right: none; }
`;

export const EventBlock = styled.div<{ $preview?: boolean }>`
  position: absolute;
  left: 2px;
  right: 2px;
  box-sizing: border-box;
  background: ${({ $preview }) =>
    $preview ? fade(SECONDARY, 0.3) : fade(SECONDARY, 0.6)};
  border: 1px solid ${fade(SECONDARY, 0.8)};
  border-radius: 3px;
  cursor: grab;
  z-index: 1;
  min-height: ${SLOT_HEIGHT}px;
  overflow: hidden;
  &:hover { background: ${fade(SECONDARY, 0.8)}; }
  &:hover > [data-delete] { opacity: 1; }
`;

export const DeleteButton = styled.div`
  position: absolute;
  top: 1px;
  right: 1px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${cuideoTheme.palette.error.main};
  color: #fff;
  font-size: calc(0.55rem * var(--wc-font-scale, 1));
  line-height: 14px;
  text-align: center;
  cursor: pointer;
  opacity: 0;
  z-index: 3;
  &:hover { background: ${COLOR_ERROR_HOVER}; }
`;

export const EventLabel = styled.span`
  display: block;
  font-size: calc(0.85rem * var(--wc-font-scale, 1));
  line-height: 1.2;
  color: ${COLOR_EVENT_LABEL};
  font-weight: 400;
  padding: 1px 3px;
  pointer-events: none;
  white-space: nowrap;
`;

export const ResizeHandle = styled.div<{ $position: "top" | "bottom" }>`
  position: absolute;
  left: 0;
  right: 0;
  height: 5px;
  cursor: ns-resize;
  z-index: 2;
  ${({ $position }) => ($position === "top" ? "top: 0;" : "bottom: 0;")}
`;

// Rayado gris de los bloques «Ocupado» (contratos de la candidata), distinto del
// color de las franjas de disponibilidad declaradas.
const OCCUPIED_COLOR = cuideoTheme.palette.grey[500];
export const OCCUPIED_BORDER_COLOR = cuideoTheme.palette.grey[600];
export const OCCUPIED_TEXT_COLOR = cuideoTheme.palette.grey[800];
export const OCCUPIED_BACKGROUND = `repeating-linear-gradient(
    45deg,
    ${fade(OCCUPIED_COLOR, 0.35)} 0px,
    ${fade(OCCUPIED_COLOR, 0.35)} 6px,
    ${fade(OCCUPIED_COLOR, 0.15)} 6px,
    ${fade(OCCUPIED_COLOR, 0.15)} 12px
  )`;

// Por encima de BlockedSlotOverlay (se pinta después) y por debajo de las franjas
// declaradas (EventBlock), para que su ✕ siga accesible.
export const OccupiedEventBlock = styled.div`
  position: absolute;
  left: 2px;
  right: 2px;
  box-sizing: border-box;
  background-image: ${OCCUPIED_BACKGROUND};
  border: 1px solid ${OCCUPIED_BORDER_COLOR};
  border-radius: 3px;
  cursor: not-allowed;
  z-index: 0;
  overflow: hidden;
`;

export const OccupiedLabel = styled.span`
  display: block;
  font-size: calc(0.75rem * var(--wc-font-scale, 1));
  line-height: 1.2;
  color: ${OCCUPIED_TEXT_COLOR};
  font-weight: 600;
  padding: 1px 3px;
  pointer-events: none;
  white-space: normal;
`;

export const BlockedSlotOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  background-color: ${COLOR_SURFACE_DISABLED};
  background-image: ${SLOT_GRID};
  pointer-events: none;
  z-index: 0;
`;
